import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { patch, post } from "../../lib/api";
import { sortPermissions, ungrantable } from "../../lib/permissions";
import { ROLE_NAME_MAX, nameTaken, roleFormIssues } from "../../lib/roles";
import { useSession } from "../../lib/session";
import type { Role } from "../../lib/types";
import { Button } from "../ui/Button";
import { ErrorNotice } from "../ui/Feedback";
import { Field } from "../ui/Input";
import { MicroLabel } from "../ui/Text";
import { PermissionPicker } from "./PermissionPicker";

/**
 * Creating a role, or changing one.
 *
 * Editing sends only what changed, for `EditOperator`'s reason. A permission
 * change replaces the role's whole set on the server and reaches every holder of
 * the role on their next request — the form says so beside the button, because
 * that is the one consequence of this form that is not on screen.
 *
 * The no-escalation rule is enforced in the picker (locked boxes) and once more
 * here: a role that somehow carries a permission the actor lacks is not offered for
 * saving with a changed permission set, since the server would refuse it with 403.
 */
export function RoleForm({
  role,
  existing,
  onSaved,
  onCancel,
}: {
  /** Absent to create. */
  readonly role?: Role | undefined;
  /** Every role, so a taken name is said while typing rather than after a 409. */
  readonly existing: readonly Role[];
  readonly onSaved: (role: Role) => void;
  readonly onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const { principal } = useSession();
  const held = principal?.permissions ?? [];

  const [name, setName] = useState(role?.name ?? "");
  const [description, setDescription] = useState(role?.description ?? "");
  const [permissions, setPermissions] = useState<readonly string[]>(role?.permissions ?? []);

  const save = useMutation({
    mutationFn: (body: { name?: string; description?: string; permissions?: readonly string[] }) =>
      role === undefined ? post<Role>("/roles", body) : patch<Role>(`/roles/${role.id}`, body),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ["roles"] });
      // Operators' effective permissions are derived from roles.
      void queryClient.invalidateQueries({ queryKey: ["operators"] });
      onSaved(saved);
    },
  });

  const trimmedName = name.trim();
  const trimmedDescription = description.trim();
  const nameChanged = trimmedName !== (role?.name ?? "");
  const descriptionChanged = trimmedDescription !== (role?.description ?? "");
  const sorted = sortPermissions(permissions);
  const permissionsChanged =
    role === undefined || sorted.join(",") !== sortPermissions(role.permissions).join(",");

  const typed = roleFormIssues({ name });
  const issues =
    typed.name === undefined && trimmedName !== "" && nameTaken(existing, trimmedName, role?.id)
      ? { name: "Another role already has this name." }
      : typed;
  const blocked = permissionsChanged ? ungrantable(held, sorted) : [];
  const changed = role === undefined || nameChanged || descriptionChanged || permissionsChanged;

  const canSubmit =
    changed &&
    trimmedName !== "" &&
    issues.name === undefined &&
    blocked.length === 0 &&
    !save.isPending;

  return (
    <div className="flex flex-col gap-4 border border-rule bg-paper px-4 py-4">
      <div className="flex items-baseline justify-between gap-4">
        <MicroLabel>{role === undefined ? "New role" : `Edit ${role.name}`}</MicroLabel>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>

      {/* 409 is a taken name; 403 is a permission the actor lacks. Both are the
          server's own sentence. */}
      {save.isError ? <ErrorNotice error={save.error} /> : null}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!canSubmit) return;
          save.mutate(
            role === undefined
              ? {
                  name: trimmedName,
                  ...(trimmedDescription === "" ? {} : { description: trimmedDescription }),
                  permissions: sorted,
                }
              : {
                  ...(nameChanged ? { name: trimmedName } : {}),
                  ...(descriptionChanged ? { description: trimmedDescription } : {}),
                  ...(permissionsChanged ? { permissions: sorted } : {}),
                },
          );
        }}
        className="flex flex-col gap-5"
      >
        <div className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2">
          <Field
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={ROLE_NAME_MAX}
            required
            autoFocus
            hint="Unique, whatever the capitalisation."
            {...(issues.name === undefined ? {} : { error: issues.name })}
          />
          <Field
            label="Description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            maxLength={200}
            hint="Who this role is for. Shown when assigning it."
          />
        </div>

        <PermissionPicker
          legend={`Permissions · ${sorted.length} chosen`}
          value={sorted}
          held={held}
          onChange={setPermissions}
        />

        {blocked.length === 0 ? null : (
          <p role="alert" className="m-0 text-micro tracking-normal normal-case text-signal">
            This role carries {blocked.length} permission{blocked.length === 1 ? "" : "s"} you don't
            hold, so you can rename it but not change what it grants.
          </p>
        )}

        <div className="flex flex-wrap items-baseline gap-4">
          <Button type="submit" variant="primary" disabled={!canSubmit} pending={save.isPending}>
            {role === undefined ? "Create role" : "Save changes"}
          </Button>
          {role === undefined || role.memberCount === 0 ? null : (
            <span className="text-micro tracking-normal normal-case text-ink-faint">
              Applies to {role.memberCount} operator{role.memberCount === 1 ? "" : "s"} on their
              next request.
            </span>
          )}
        </div>
      </form>
    </div>
  );
}
